import React from 'react';
import { ethers } from 'ethers';
import { fmt, reasonText } from '../lib';
import Info from './Info';
import { short, cdShort } from './SeasonPanel';

// "Build a Gen1 ship" panel on the Fleet page. Pure layout: the Fleet page reads the chain (Shipyard quote/cap/odds,
// Fleet powers, EmissionReserve rate) and passes it in; Build calls the page's existing mintGen1 action.
const usd = (wei) => `$${Number(ethers.formatEther(wei ?? 0n)).toLocaleString(undefined, { maximumSignificantDigits: 3 })}`;
const span = (s) => (s >= 3600 ? `${+(s / 3600).toFixed(1)}h` : `${Math.round(s / 60)} min`);
const pct = (bps) => `${Number(bps) / 100}%`;

export default function Gen1Panel({ err, onRetry, d, n, setN, ore, account, busy, onBuild, now, children }) {
  // d: { start, delay, open, cap, today, perTx, left, hasId, quote: {ok, why, cost, usd}, pirateBps, hijackBps, minerW, pirateW, powers, window, hint }
  if (!d && err) return <div className="card g1" data-testid="gen1"><h3 className="g1-title">Build a Gen1 ship</h3><p className="g1-lead" data-testid="gen1-err">Couldn't load ship-building data from the network. <button className="action-btn" onClick={onRetry}>Retry</button></p>{children}</div>;
  if (!d) return <div className="card g1" data-testid="gen1"><h3 className="g1-title">Build a Gen1 ship</h3><p className="g1-lead">Loading…</p>{children}</div>;
  const q = d.quote || {};
  const notOpen = !d.start || now < d.start;
  const capLeft = Math.max(0, d.cap - d.today);
  const max = Math.max(1, Math.min(d.perTx, capLeft || 1, d.left || d.perTx));
  const per = q.ok && n > 0 ? q.cost / BigInt(n) : null;
  const perUsd = q.usd !== undefined && n > 0 ? q.usd / BigInt(n) : null;
  const short1 = ore !== null && ore !== undefined && q.ok && ore < q.cost;
  const nextUtc = (Math.floor(now / 86400) + 1) * 86400;
  const resetAt = new Date(nextUtc * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  let why = '';
  if (!d.start) why = `Opens ${span(d.delay)} after the ORE pool opens.`;
  else if (notOpen) why = `Opens in ${cdShort(d.start - now)} (${span(d.delay)} after the ORE pool opened).`;
  else if (!account) why = 'Connect your wallet to build.';
  else if (!d.hasId) why = 'Building needs an Agent ID in this wallet.';
  else if (capLeft <= 0) why = `Today's ${d.cap} ships are built. Building reopens at ${resetAt}.`;
  else if (q.ok === false) why = q.why === 'NetworkBusy' ? 'Price unavailable right now. Try again in a moment.' : reasonText(q.why);
  else if (short1) why = `Not enough ORE: you need ${short(q.cost - ore)} more.`;
  const can = !why && q.ok && !busy;

  const minerPct = (10000 - d.pirateBps) / 100;
  const tiers = ['Common', 'Rare', 'Epic'];
  const wSum = (w) => w.reduce((a, b) => a + b, 0);
  const oddsTxt = (w) => tiers.map((t, i) => Math.round((w[i] * 100) / wSum(w))).join(' / ') + '%';
  const rows = [
    ['Miner / Pirate', `${minerPct}% / ${pct(d.pirateBps)}`],
    ...(d.minerW.join() === d.pirateW.join() ? [['Common/Rare/Epic', oddsTxt(d.minerW)]] : [['Miner C/R/E', oddsTxt(d.minerW)], ['Pirate C/R/E', oddsTxt(d.pirateW)]]),
    ['Miner power', `${d.powers.join(' / ')}`],
    ['Pirate rank', '5 / 6 / 7'],
    ['Hijack risk', pct(d.hijackBps)],
    ['ORE burned', '100%'],
    ['Price', `min(${d.window >= 3600 ? `${d.window / 3600}h` : `${Math.round(d.window / 60)}m`} avg, live)`],
    ['Daily limit', `${d.cap} / day`],
  ];
  const h = d.hint;

  return (
    <div className="card g1" data-testid="gen1">
      <h3 className="g1-title">Build a Gen1 ship <Info label="Gen1 ships" rows={rows} testid="gen1-info" /></h3>
      <p className="g1-lead">Spend ORE to build a new random ship. The ORE you pay is burned.</p>
      <p className="g1-why" data-testid="gen1-benefit">More ships, more ORE: a new miner adds {d.powers[0]}–{d.powers[d.powers.length - 1]} power to your share of the daily ORE; a new pirate gets a cut of the pirate tax.</p>
      {h && <p className="g1-est" data-testid="gen1-estimate"><span>Estimate</span>{h.full
        ? `You already have 100% of the mining power, so a Common miner adds no ORE today. It keeps your share up when others join.`
        : `A Common miner (${d.powers[0]} power) takes your share from ${h.from}% to ${h.to}%, ≈ +${short(h.plus)} ORE/day at today's rate.`}</p>}

      <div className="g1-cells">
        <div className="g1-cell"><p className="sp-label">Cost</p>
          <p className="g1-num" title={per !== null ? `${fmt(per, 0)} ORE per ship` : ''}>{perUsd !== null ? usd(perUsd) : '—'}<span className="sp-unit"> per ship</span></p>
          <p className="g1-sub">{per !== null ? `≈ ${short(per)} ORE` : notOpen ? 'priced when open' : '…'}</p></div>
        <div className="g1-cell"><p className="sp-label">You have</p>
          <p className="g1-num" title={ore !== null && ore !== undefined ? `${fmt(ore, 0)} ORE` : ''}>{account && ore !== null && ore !== undefined ? short(ore) : '—'}<span className="sp-unit"> ORE</span></p>
          <p className="g1-sub">{per !== null && account && ore ? `enough for ${String(ore / per)}` : '\u00a0'}</p></div>
        <div className="g1-cell"><p className="sp-label">Today</p>
          <p className="g1-num" data-testid="gen1-today">{d.today}<span className="sp-unit"> / {d.cap} built</span></p>
          <p className="g1-sub">{capLeft} left · resets {resetAt}</p></div>
      </div>

      {notOpen ? <p className="g1-closed" data-testid="gen1-wait">{why}</p> : (
        <div className="g1-buy">
          <div className="m1-stepper">
            <button aria-label="fewer" onClick={() => setN(Math.max(1, n - 1))} disabled={n <= 1}>−</button>
            <b data-testid="gen1-qty">{n}</b>
            <button aria-label="more" onClick={() => setN(Math.min(max, n + 1))} disabled={n >= max}>+</button>
          </div>
          <div className="g1-total"><span className="sp-label">Total</span>
            <b title={q.ok ? `${fmt(q.cost, 0)} ORE` : ''} data-testid="gen1-ore">{q.ok ? `${short(q.cost)} ORE` : '…'}</b>
            {q.usd !== undefined && <small>{usd(q.usd)}</small>}</div>
          <button className="action-btn g1-btn" data-testid="gen1-build" disabled={!can} onClick={onBuild}>Build {n > 1 ? `${n} ships` : 'ship'}</button>
          {why && <p className="g1-reason" data-testid="gen1-why">{why}</p>}
        </div>
      )}
      {children}
    </div>
  );
}
